import { ArrowLeftOutlined } from '@ant-design/icons';
import { Button, Checkbox, Form, Input, message, Select } from 'antd';
import { FC, ReactNode, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LayoutSite } from '../components/layout';
import { Loader } from '../components/loader';
import { addmulti_venue, get_sources_list } from '../services/api.service';
import { APIAnswerData, VenueInfo } from '../types';
import './importvenues.scss';

/* -------------------------------------------------------------------------------- *\
|                               PreviewFile
\* -------------------------------------------------------------------------------- */
class PreviewVenue
{
    public sic: string = "";
    public name: string = "";
    public selected: boolean = false;
    public message: string = "";
}
/* -------------------------------------------------------------------------------- *\
|                               ImportVenuesPage
\* -------------------------------------------------------------------------------- */
export const ImportVenuesPage: FC<{ children?: ReactNode }> = ({ children }) =>
{
    const [loading, setLoading] = useState(true);
    const [previewfiles, setPreviewFiles] = useState<PreviewVenue[]>([]);
    const navigate = useNavigate();
    const [inputfilekey, setinputfilekey] = useState<string>('key_0');
    const [sources, setSources] = useState<string[]>([]);

    /* -------------------------------------------------------------------------------- *\
    |                               effects
    \* -------------------------------------------------------------------------------- */
    useEffect(() =>
    {
        setLoading(true);
        get_sources_list().then((sources: string[]) =>
        {
            setSources(sources);
            setLoading(false);
        })
        .catch(() =>
        {
            message.error("Error loading sources");
            setLoading(false);
        });
        // eslint-disable-next-line
    }, [])


    /* -------------------------------------------------------------------------------- *\
    |                               handleUpload
    \* -------------------------------------------------------------------------------- */
    function handleUpload(event)
    {
        const e: HTMLInputElement = event.target;
        if (e.files && e.files[0])
        {
            const f: File = e.files[0];

            var reader = new FileReader();
            reader.addEventListener('load', (e) =>
            {
                if (e.loaded === e.total)
                {
                    const content = e.target?.result as string;
                    const lines: string[] = content.split("\n");

                    const venues: PreviewVenue[] = [];
                    for(const line of lines)
                    {
                        const venue: PreviewVenue = new PreviewVenue();
                        const els: string[] = line.split(';');
                        if (els.length !== 2) continue;

                        venue.sic = els[0];
                        venue.name = els[1];
                        venues.push(venue);
                    }
                    setPreviewFiles(venues);
                }
            });
            reader.readAsText(f as Blob);
            setinputfilekey(inputfilekey.split('_')[0] + '_' + (parseInt(inputfilekey.split('_')[1]) + 1));
        }
    }
    /* -------------------------------------------------------------------------------- *\
    |                               selectall
    \* -------------------------------------------------------------------------------- */
    function selectall()
    {
        for(const f of previewfiles)
        {
            f.selected = true;
        }
        setPreviewFiles([...previewfiles]);
    }
    /* -------------------------------------------------------------------------------- *\
    |                               deselectall
    \* -------------------------------------------------------------------------------- */
    function deselectall()
    {
        for(const f of previewfiles)
        {
            f.selected = false;
        }
        setPreviewFiles([...previewfiles]);
    }
    /* -------------------------------------------------------------------------------- *\
    |                               onFinishUpload
    \* -------------------------------------------------------------------------------- */
    function onFinishUpload(values: any)
    {
        const selected: PreviewVenue[] = previewfiles.filter(f => f.selected);
        if (selected.length === 0)
        {
            message.error("Please select at least one venue");
            return;
        }
        const source: string = values.source;
        if (!source)
        {
            message.error("Please select a source");
            return;
        }

        const infos: VenueInfo[] = selected.map(f => { return { sic: f.sic, name: f.name } });

        addmulti_venue(infos, source)
        .then((answer: APIAnswerData) =>
        {
            if (answer.status)
            {
                const answers: APIAnswerData[] = answer.data as APIAnswerData[];

                for (const a of answers)
                {
                    const code: string = a.data as string;
                    const f = previewfiles.find(f => f.sic === code);
                    if (f)
                    {
                        f.message = a.message;
                    }
                    else
                    {
                        message.warning("Unknown venue " + code);
                    }
                }
                setPreviewFiles([...previewfiles]);
                message.info("import succeded");
            }
            else
            {
                message.error("An error occured while importing: " + answer.message);
            }
        })
        .catch((error) => { message.info(error); })
    }

    return (
        <LayoutSite>
            <div className="importvenues">
                {loading ? <Loader /> : <></>}
                <div className="importvenues__header">
                    <div>
                        <button className="importvenues__header__back" onClick={() => navigate("/tracks")}>
                            <ArrowLeftOutlined />
                            back
                        </button>
                    </div>
                </div>
                <div className="importvenues__body">
                    <div className="importvenues__body__title">Import venues</div>
                    <Form name="importvenues__body__form" className="importvenues__body__form" onFinish={onFinishUpload}>
                        <Form.Item label='Source' name='source' >
                            <Select style={{ width: 150 }}>
                                {
                                    sources.map((v,i) => 
                                    {
                                        return (
                                            <Select.Option key={'source_' + i} value={v}>{v}</Select.Option>
                                        )
                                    })
                                }
                            </Select>
                        </Form.Item>
                        <Form.Item label='File'>
                            <Input type="file" hidden={true} onChange={handleUpload} accept=".csv" key={inputfilekey} />
                        </Form.Item>

                        <Form.Item className="importvenues__body__form__toolbar">
                            <Button type="primary" htmlType="submit">Import</Button>
                        </Form.Item>
                    </Form>
                    {
                        previewfiles.length > 0 &&
                        <>
                            <div className='importus__body__preview__buttons'>
                                <Button size='small' onClick={() => selectall()}>Select all</Button>
                                <Button size='small' onClick={() => deselectall()}>Deselect all</Button>
                                <Button size='small' onClick={() => document.querySelector('form#importvenues__body__form')?.dispatchEvent(new MouseEvent('submit', { bubbles: true, cancelable: true }))}>Import</Button>
                            </div>
                            <div className='importvenues__body__preview'>
                                <div className='importvenues__body__preview__files'>
                                    {
                                        previewfiles?.map((file: PreviewVenue, index) => 
                                        {
                                            return (
                                                <div className={'importvenues__body__preview__files__file'} key={'file_' + index}>
                                                    <div><Checkbox checked={file.selected} onChange={(e) => { file.selected = e.target.checked; setPreviewFiles([...previewfiles]); }} /></div>
                                                    <div>{file.sic}</div>
                                                    <div>{file.name}</div>
                                                    <div>{file.message}</div>
                                                </div>
                                            )
                                        })
                                    }
                                </div>
                            </div>
                        </>
                    }
                </div>
            </div>
        </LayoutSite>
    );
}
